import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { 
  Check, 
  AlertCircle, 
  Home, 
  Truck, 
  FileText, 
  MapPin, 
  DollarSign, 
  Heart, 
  CheckCircle, 
  ChevronRight, 
  ChevronDown,
  ChevronLeft,
  Info,
  Star,
  Award,
  Calendar,
  Clock,
  Users,
  Map,
  Compass,
  Flag,
  Target,
  Key,
  Lock,
  Mail,
  Phone,
  Building,
  Package,
  ShoppingCart,
  Gift,
  X,
  ZoomIn,
  Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/App';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Comprehensive icon mapping - matches admin panel options
const iconMap = {
  Home,
  Truck,
  FileText,
  MapPin,
  DollarSign,
  Heart,
  CheckCircle,
  AlertCircle,
  Info,
  Star,
  Award,
  Calendar,
  Clock,
  Users,
  Map,
  Compass,
  Flag,
  Target,
  Key,
  Lock,
  Mail,
  Phone,
  Building,
  Package,
  ShoppingCart,
  Gift
};

const Adopt = ({ user, logout }) => {
  const [mustangs, setMustangs] = useState([]);
  const [facility, setFacility] = useState(null);
  const [selectedMustang, setSelectedMustang] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [adoptionGuide, setAdoptionGuide] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [viewMode, setViewMode] = useState('guide'); // 'guide' or 'mustangs'
  
  // Photo gallery modal state
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [galleryMustang, setGalleryMustang] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    fetchMustangs();
    fetchFacility();
    fetchAdoptionGuide();
  }, []);

  const fetchMustangs = async () => {
    try {
      const response = await api.get('/mustangs/available');
      console.log('Fetched mustangs:', response.data?.length || 0);
      setMustangs(response.data || []);
    } catch (error) {
      console.error('Error fetching mustangs:', error);
      toast.error('Failed to load mustangs');
      setMustangs([]);
    }
  };

  const fetchFacility = async () => {
    try {
      const response = await api.get('/facility/get');
      setFacility(response.data);
    } catch (error) {
      console.error('Error fetching facility:', error);
    }
  };

  const fetchAdoptionGuide = async () => {
    try {
      const response = await api.get('/adoption-guide');
      setAdoptionGuide(response.data);
    } catch (error) {
      console.error('Error fetching adoption guide:', error);
    }
  };

  const handleAdopt = async () => {
    if (!facility?.qualified) {
      toast.error('Your facility must be qualified first! Visit the Facility Builder.');
      return;
    }

    try {
      // Use the user-mustangs/adopt-virtual endpoint which properly adds to the herd
      const response = await api.post('/user-mustangs/adopt-virtual', {
        virtual_mustang_id: selectedMustang.id
      });
      toast.success(`Successfully adopted ${selectedMustang.name}! 🎉`);
      setShowDialog(false);
      
      // Redirect to the herd to see the new mustang
      setTimeout(() => {
        window.location.href = '/the-herd';
      }, 1500);
    } catch (error) {
      console.error('Adoption error:', error);
      toast.error(error.response?.data?.detail || 'Failed to adopt mustang');
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Heart;
    return IconComponent;
  };

  // Get all photos for a mustang
  const getMustangPhotos = (mustang) => {
    const photos = [];
    // Add primary photo
    if (mustang.photo_url) {
      photos.push(mustang.photo_url);
    }
    // Add additional photos from photo_urls array
    if (mustang.photo_urls && Array.isArray(mustang.photo_urls)) {
      mustang.photo_urls.forEach(url => {
        if (url && !photos.includes(url)) {
          photos.push(url);
        }
      });
    }
    // Fallback to image_url if no photos
    if (photos.length === 0 && mustang.image_url) {
      photos.push(mustang.image_url);
    }
    return photos;
  };

  // Open photo gallery for a mustang
  const openPhotoGallery = (mustang) => {
    setGalleryMustang(mustang);
    setCurrentPhotoIndex(0);
    setShowPhotoGallery(true);
  };

  // Navigate to next photo
  const nextPhoto = () => {
    if (!galleryMustang) return;
    const photos = getMustangPhotos(galleryMustang);
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  // Navigate to previous photo
  const prevPhoto = () => {
    if (!galleryMustang) return;
    const photos = getMustangPhotos(galleryMustang);
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  // Get full image URL (not thumbnail) for gallery view
  const getFullImageUrl = (url) => {
    if (!url) return null;
    // Remove /thumb/ if present to get full image
    const fullUrl = url.replace('/api/media/thumb/', '/api/media/');
    return fullUrl.startsWith('http') ? fullUrl : `${process.env.REACT_APP_BACKEND_URL}${fullUrl}`;
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{color: '#D66238'}}>
            Adoption Center
          </h1>
          <p className="text-lg" style={{color: '#372F2F'}}>
            Learn about the adoption process and find your perfect mustang
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-8">
          <Button
            onClick={() => setViewMode('guide')}
            style={{
              backgroundColor: viewMode === 'guide' ? '#6FCFD3' : 'white',
              color: viewMode === 'guide' ? 'white' : '#6FCFD3',
              border: '2px solid #6FCFD3'
            }}
          >
            📚 View Adoption Guide
          </Button>
          <Button
            onClick={() => window.location.href = '/virtual-corral'}
            className="flex items-center gap-2"
            style={{
              backgroundColor: '#D66238',
              color: 'white',
              border: '2px solid #D66238'
            }}
          >
            <Award className="w-5 h-5" />
            Go to Virtual Corral
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Adoption Guide View */}
        {viewMode === 'guide' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <h2 className="text-2xl font-bold mb-4" style={{color: '#D66238'}}>
                Step-by-Step Adoption Process
              </h2>
              <p className="text-gray-600 mb-4">
                Follow these steps to successfully adopt a wild mustang or burro. Click on each section to learn more.
              </p>
            </div>

            {adoptionGuide.map((section, index) => {
              const Icon = getIcon(section.icon);
              const isExpanded = expandedSection === section.id;

              return (
                <div
                  key={section.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden transition-all"
                  style={{border: isExpanded ? '3px solid #6FCFD3' : '2px solid #e5e7eb'}}
                >
                  {/* Section Header */}
                  <button
                    onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                    className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-4">
                      {/* Show custom icon image if available, otherwise show pre-made icon */}
                      {section.icon_image_url ? (
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center p-3"
                          style={{backgroundColor: '#6FCFD3'}}
                        >
                          <img 
                            src={section.icon_image_url} 
                            alt={section.title}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{backgroundColor: '#6FCFD3'}}
                        >
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold" style={{color: '#6FCFD3'}}>
                            STEP {section.order}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold" style={{color: '#D66238'}}>
                          {section.title}
                        </h3>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-6 h-6" style={{color: '#6FCFD3'}} />
                    ) : (
                      <ChevronRight className="w-6 h-6 text-gray-400" />
                    )}
                  </button>

                  {/* Section Content */}
                  {isExpanded && (
                    <div className="px-6 pb-6 pt-2">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: section.content }}
                        style={{color: '#372F2F'}}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            <div className="bg-white rounded-lg shadow-lg p-6 mt-8" style={{border: '2px solid #D66238'}}>
              <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                Ready to Adopt?
              </h3>
              <p className="text-gray-600 mb-4">
                Once you've reviewed the adoption process and prepared your facilities, you can browse available mustangs and start your adoption journey!
              </p>
              <Button
                onClick={() => setViewMode('mustangs')}
                style={{backgroundColor: '#6FCFD3', color: 'white'}}
                className="w-full sm:w-auto"
              >
                Browse Available Mustangs
              </Button>
            </div>
          </div>
        )}

        {/* Mustangs View - Shows Virtual Mustangs */}
        {viewMode === 'mustangs' && (
          <>
            {/* Back button */}
            <Button
              onClick={() => setViewMode('guide')}
              variant="outline"
              className="mb-6"
              style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
            >
              ← Back to Adoption Guide
            </Button>

            {/* Facility Status */}
            {facility && (
              <div 
                className="p-6 rounded-xl mb-8"
                style={{
                  backgroundColor: facility.qualified ? '#d1fae5' : '#fee2e2',
                  border: `2px solid ${facility.qualified ? '#10b981' : '#ef4444'}`
                }}
              >
                <div className="flex items-center gap-3">
                  {facility.qualified ? (
                    <>
                      <Check className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-bold text-green-800">Facility Qualified</p>
                        <p className="text-sm text-green-700">You're ready to adopt a mustang!</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-6 h-6 text-red-600" />
                      <div>
                        <p className="font-bold text-red-800">Facility Not Qualified</p>
                        <p className="text-sm text-red-700">Complete your facility setup to adopt</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Mustangs Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mustangs.map((mustang) => {
                // Get all photos for this mustang
                const allPhotos = getMustangPhotos(mustang);
                const hasMultiplePhotos = allPhotos.length > 1;
                
                // Get the best available image for thumbnail
                const imageUrl = allPhotos[0] || null;
                
                // Generate thumbnail URL if it's an API media URL
                const thumbUrl = imageUrl && imageUrl.includes('/api/media/') 
                  ? imageUrl.replace('/api/media/', '/api/media/thumb/') 
                  : imageUrl;
                
                return (
                  <div 
                    key={mustang.id}
                    className="rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow"
                    style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}
                  >
                    {/* Clickable image area */}
                    <div 
                      className="relative cursor-pointer group"
                      onClick={() => openPhotoGallery(mustang)}
                    >
                      {imageUrl ? (
                        <img 
                          src={thumbUrl} 
                          alt={mustang.name}
                          loading="lazy"
                          className="w-full h-64 object-cover transition-transform group-hover:scale-105"
                          onError={(e) => {
                            // First try full image if thumbnail fails
                            if (e.target.src.includes('/thumb/')) {
                              e.target.src = imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
                            } else {
                              e.target.onerror = null;
                              e.target.src = 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=400';
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400 text-4xl">🐴</span>
                        </div>
                      )}
                      
                      {/* Photo count badge */}
                      {hasMultiplePhotos && (
                        <div className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-bold bg-black/60 text-white flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" />
                          {allPhotos.length}
                        </div>
                      )}
                      
                      {/* View photos overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                          <ZoomIn className="w-4 h-4" style={{color: '#6FCFD3'}} />
                          <span className="text-sm font-medium" style={{color: '#372F2F'}}>View Photos</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>
                        {mustang.name}
                      </h3>
                      <div className="space-y-2 mb-4 text-sm">
                        <p><span className="font-semibold">Origin:</span> {mustang.herd_origin || mustang.hma || 'Unknown'}</p>
                        <p><span className="font-semibold">Age:</span> {mustang.age} years</p>
                        <p><span className="font-semibold">Gender:</span> {mustang.gender}</p>
                        <p><span className="font-semibold">Color:</span> {mustang.coat_color || mustang.color || 'Unknown'}</p>
                        {mustang.height && <p><span className="font-semibold">Height:</span> {mustang.height}</p>}
                        {mustang.adoption_method && (
                          <p><span className="font-semibold">Adoption Method:</span> {mustang.adoption_method.toUpperCase()}</p>
                        )}
                      </div>
                      {(mustang.description || mustang.temperament || mustang.disposition) && (
                        <p className="text-sm mb-4" style={{color: '#372F2F'}}>
                          {mustang.description || `Temperament: ${mustang.temperament || mustang.disposition}`}
                        </p>
                      )}
                      
                      {/* Action buttons */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openPhotoGallery(mustang)}
                          variant="outline"
                          className="flex-1"
                          style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                        >
                          <ZoomIn className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        <Button
                          onClick={async () => {
                            if (!facility?.qualified) {
                              toast.error('Your facility must be qualified first! Visit the Facility Builder.');
                              return;
                            }
                            try {
                              await api.post('/user-mustangs/adopt-virtual', {
                                virtual_mustang_id: mustang.id
                              });
                              toast.success(`Successfully adopted ${mustang.name}! 🎉`);
                              setTimeout(() => {
                                window.location.href = '/the-herd';
                              }, 1500);
                            } catch (error) {
                              console.error('Adoption error:', error);
                              toast.error(error.response?.data?.detail || 'Failed to adopt mustang');
                            }
                          }}
                          className="flex-1"
                          style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                        >
                          <Heart className="w-4 h-4 mr-1" />
                          Adopt
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {mustangs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-xl" style={{color: '#D66238'}}>
                  No mustangs available for adoption at this time
                </p>
                <p className="text-gray-600 mt-2">Check back soon!</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Adoption Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              Adopt {selectedMustang?.name}?
            </DialogTitle>
            <DialogDescription>
              Are you ready to welcome {selectedMustang?.name} to your ranch?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!facility?.qualified && (
              <div 
                className="p-4 rounded-lg"
                style={{backgroundColor: '#fee2e2', border: '1px solid #ef4444'}}
              >
                <p className="text-sm text-red-800">
                  Your facility is not yet qualified. Please complete the Facility Builder first.
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDialog(false)}
                variant="outline"
                style={{borderColor: '#D66238', color: '#D66238'}}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdopt}
                disabled={!facility?.qualified}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                Confirm Adoption
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Gallery Dialog */}
      <Dialog open={showPhotoGallery} onOpenChange={setShowPhotoGallery}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          {galleryMustang && (
            <>
              {/* Header */}
              <div className="p-4 border-b flex justify-between items-center" style={{backgroundColor: '#6FCFD3'}}>
                <div>
                  <h2 className="text-2xl font-bold text-white">{galleryMustang.name}</h2>
                  <p className="text-white/80 text-sm">
                    {galleryMustang.color || galleryMustang.coat_color} {galleryMustang.gender} • {galleryMustang.age} years old
                  </p>
                </div>
                <button 
                  onClick={() => setShowPhotoGallery(false)}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
              
              {/* Photo Display */}
              <div className="relative bg-black">
                {getMustangPhotos(galleryMustang).length > 0 ? (
                  <>
                    <img 
                      src={getFullImageUrl(getMustangPhotos(galleryMustang)[currentPhotoIndex])}
                      alt={`${galleryMustang.name} - Photo ${currentPhotoIndex + 1}`}
                      className="w-full h-[50vh] object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=800';
                      }}
                    />
                    
                    {/* Navigation arrows - only show if multiple photos */}
                    {getMustangPhotos(galleryMustang).length > 1 && (
                      <>
                        <button 
                          onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                        
                        {/* Photo counter */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white text-sm">
                          {currentPhotoIndex + 1} / {getMustangPhotos(galleryMustang).length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-[50vh] flex items-center justify-center bg-gray-100">
                    <div className="text-center">
                      <span className="text-6xl mb-4 block">🐴</span>
                      <p className="text-gray-500">No photos available</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Thumbnail strip - only show if multiple photos */}
              {getMustangPhotos(galleryMustang).length > 1 && (
                <div className="p-3 bg-gray-100 border-t overflow-x-auto">
                  <div className="flex gap-2 justify-center">
                    {getMustangPhotos(galleryMustang).map((photo, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPhotoIndex(idx)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                          idx === currentPhotoIndex 
                            ? 'border-[#6FCFD3] ring-2 ring-[#6FCFD3]/50' 
                            : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img 
                          src={photo.includes('/api/media/') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${photo.replace('/api/media/', '/api/media/thumb/')}`
                            : photo
                          }
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Mustang details */}
              <div className="p-4 border-t bg-white">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                  <div>
                    <span className="font-semibold block" style={{color: '#D66238'}}>Origin</span>
                    <span>{galleryMustang.herd_origin || galleryMustang.hma || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="font-semibold block" style={{color: '#D66238'}}>Age</span>
                    <span>{galleryMustang.age} years</span>
                  </div>
                  <div>
                    <span className="font-semibold block" style={{color: '#D66238'}}>Gender</span>
                    <span>{galleryMustang.gender}</span>
                  </div>
                  <div>
                    <span className="font-semibold block" style={{color: '#D66238'}}>Color</span>
                    <span>{galleryMustang.coat_color || galleryMustang.color || 'Unknown'}</span>
                  </div>
                </div>
                
                {/* Freezemark - displayed prominently if available */}
                {galleryMustang.freezemark && (
                  <div className="mb-4 p-3 rounded-lg" style={{backgroundColor: '#f0f9f9', border: '1px solid #6FCFD3'}}>
                    <span className="font-semibold text-sm" style={{color: '#D66238'}}>Freezemark ID:</span>
                    <span className="ml-2 font-mono text-lg font-bold" style={{color: '#372F2F'}}>{galleryMustang.freezemark}</span>
                  </div>
                )}
                
                {(galleryMustang.description || galleryMustang.temperament || galleryMustang.disposition) && (
                  <p className="text-sm mb-4" style={{color: '#372F2F'}}>
                    {galleryMustang.description || `Temperament: ${galleryMustang.temperament || galleryMustang.disposition}`}
                  </p>
                )}
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowPhotoGallery(false)}
                    variant="outline"
                    className="flex-1"
                    style={{borderColor: '#D66238', color: '#D66238'}}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      setShowPhotoGallery(false);
                      setSelectedMustang(galleryMustang);
                      setShowDialog(true);
                    }}
                    className="flex-1"
                    style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Adopt {galleryMustang.name}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Adopt;
